import { ModalBaseDirective } from '$core/base/_modal.component';
import { Pagination } from '$core/models/pagination';
import { SwalService } from '$core/shell/swal/swal.service';
import { SwalErrorService } from '$shared/components/swal-error/swal-error.service';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { untilDestroyed } from '$core/operators/until-destroyed.operator';
import { debounceTime, finalize } from 'rxjs/operators';
import { BatchesService, BatchShort, Selectable } from '../../../batches';
import { CreateInvoiceBody, Invoice } from '../../models/invoice';
import { InvoicesService } from '../../services';
import { ShowFormedReceiptComponent } from '../show-formed-receipt/show-formed-receipt.component';
import { PaginationParams } from '$shared/components/paginator/paginator.component';

@Component({
  selector: 'app-manage-batch-receipt',
  templateUrl: './manage-batch-receipt.component.html',
  styleUrls: ['./manage-batch-receipt.component.scss'],
})
export class ManageBatchReceiptComponent
  extends ModalBaseDirective
  implements OnInit, OnDestroy
{
  public isLoading = false;
  public items: Pagination<Selectable<BatchShort>>;
  public item: Invoice;
  public selected: BatchShort[] = [];
  public searchControl: FormControl = this.fb.control(null);

  constructor(
    protected modalRef: BsModalRef,
    protected modalService: BsModalService,
    protected translateService: TranslateService,
    protected swalService: SwalService,
    protected swalErrorService: SwalErrorService,
    private invoicesService: InvoicesService,
    private batchesService: BatchesService,
    private fb: FormBuilder,
    private readonly toastr: ToastrService
  ) {
    super(modalRef, modalService, translateService);
  }

  public ngOnInit(): void {
    this.getItems();
    this.listenToSearch();
  }

  public ngOnDestroy(): void {}

  public createReceipt(): void {
    if (!this.selected.length || this.selected.length > 8) {
      const text = !this.selected.length
        ? this.translateService.instant(
            'BATCH_MAILING.RECEIPTS_TAB.ACTIONS.CREATE_NO_SELECTED'
          )
        : this.translateService.instant(
            'BATCH_MAILING.RECEIPTS_TAB.ACTIONS.CREATE_MORE_THAN_SELECTED'
          );

      this.toastr.warning(
        this.translateService.instant(
          'BATCH_MAILING.RECEIPTS_TAB.ACTIONS.CREATE_ERROR'
        ),
        text
      );

      return;
    }
    this.isLoading = true;
    const { swal } = this.swalService;
    const data: CreateInvoiceBody = {
      lists: this.selected.map(({ id }) => id),
    };

    this.invoicesService
      .createInvoice(data)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
        untilDestroyed(this)
      )
      .subscribe(
        (res) => {
          this.item = res;
          this.showFormedReceipt(res);
        },
        (err) => {
          if (err.status === 422) {
            this.swalErrorService.showError.next({
              title: 'AN_ERROR_HAS_OCCURRED',
              text: this.swalErrorService.parseError(err.error),
            });
          } else {
            swal.icon = 'error';
            swal.title = this.translateService.instant('AN_ERROR_HAS_OCCURRED');
            swal.text = this.swalErrorService.parseError(err.error);
            swal.text = err.error.message || err.statusText;
            return swal.fire();
          }
        }
      );
  }

  public showFormedReceipt(item: Invoice) {
    this.hide('saved');
    this.modalService.show(ShowFormedReceiptComponent, {
      class: 'modal-md modal-dialog-centered',
      initialState: {
        item,
      },
    });
  }

  public updateSelection(): void {
    this.selected = this.items.data.filter((el) => el.selected);
  }

  public pageChanged(ev: PaginationParams): void {
    this.getItems(ev);
  }

  private listenToSearch(): void {
    this.searchControl.valueChanges
      .pipe(debounceTime(350), untilDestroyed(this))
      .subscribe((res) => {
        const params = {
          search: res,
        };
        this.getItems(params);
      });
  }

  private getItems(params: any = {}) {
    this.isLoading = true;
    params.status = 'committed';
    params.not_included_in_receipt = 1;

    this.batchesService
      .index(params)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe((res) => {
        this.items = {
          ...res,
          data: res.data.map((item) => ({ ...item, selected: false })),
        };
      });
  }
}
